// src/context/CartContext.jsx
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import api from "../services/api";
import { useAuth } from "./AuthContext";
import { useTenant } from "./TenantContext";
import toast from "react-hot-toast";
import { trackMarketingEvent } from "../utils/marketingTracking";

const CartContext = createContext();

// Configure toast defaults globally
toast.options = {
  duration: 2000,
  position: "bottom-right",
  style: {
    background: "#333",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: "10px",
    fontSize: "14px",
    maxWidth: "350px",
  },
  success: {
    duration: 1500,
    icon: "✓",
    style: {
      background: "#10b981",
      color: "#fff",
    },
  },
  error: {
    duration: 2500,
    icon: "✗",
    style: {
      background: "#ef4444",
      color: "#fff",
    },
  },
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const { settings: tenantSettings } = useTenant();
  const [cartItems, setCartItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cart") || "[]");
    } catch (error) {
      console.error("Error hydrating cart:", error);
      return [];
    }
  });
  const [cartSubtotal, setCartSubtotal] = useState(0);
  const [cartTax, setCartTax] = useState(0);
  const [cartTaxEnabled, setCartTaxEnabled] = useState(false);
  const [cartTaxRate, setCartTaxRate] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [cartSavings, setCartSavings] = useState(0);
  const [cartOriginalTotal, setCartOriginalTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState({});
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [promotionsMap, setPromotionsMap] = useState(new Map());
  const [expiredPromotions, setExpiredPromotions] = useState([]);
  const [expiringPromotions, setExpiringPromotions] = useState([]);
  const isMounted = useRef(true);
  const fetchTimeout = useRef(null);
  const expiredNotifiedRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, []);

  // ✅ Fetch active promotions and build a map
  const fetchPromotions = useCallback(async () => {
    try {
      const response = await api.get("/api/promotions/active");
      const promotions = response.data?.promotions || [];
      const map = new Map();

      promotions.forEach((promo) => {
        const productId = promo.ProductID || promo.productId;
        if (productId) {
          map.set(productId, promo);
        }
      });

      setPromotionsMap(map);
      console.log(`📊 Loaded ${map.size} promotions for cart`);
      return map;
    } catch (error) {
      console.error("Error fetching promotions:", error);
      return new Map();
    }
  }, []);

  // Calculate totals from cart items
  const calculateTotals = useCallback((items) => {
    // Calculate subtotal with discounts applied
    const subtotal = items.reduce((sum, item) => {
      const price = parseFloat(
        item.UnitPrice || item.Price || item.SellingPrice || 0,
      );
      const quantity = parseInt(item.Quantity || 0);
      return sum + price * quantity;
    }, 0);

    // Calculate original total (without discounts)
    const originalTotal = items.reduce((sum, item) => {
      const originalPrice = parseFloat(
        item.OriginalPrice || item.UnitPrice || item.Price || 0,
      );
      const quantity = parseInt(item.Quantity || 0);
      return sum + originalPrice * quantity;
    }, 0);

    const totalSavings = Math.max(0, originalTotal - subtotal);
    const includeVat =
      tenantSettings?.IncludeVat === true ||
      tenantSettings?.IncludeVat === "true" ||
      tenantSettings?.IncludeVat === 1 ||
      tenantSettings?.IncludeVat === "1" ||
      tenantSettings?.includeVat === true ||
      tenantSettings?.includeVat === "true" ||
      tenantSettings?.includeVat === 1 ||
      tenantSettings?.includeVat === "1";
    const configuredRate = Number(
      tenantSettings?.VatRate ?? tenantSettings?.vatRate ?? 15,
    );
    const rate =
      includeVat &&
      Number.isFinite(configuredRate) &&
      configuredRate >= 0 &&
      configuredRate <= 100
        ? configuredRate
        : includeVat
          ? 15
          : 0;
    const tax = Number(((subtotal * rate) / 100).toFixed(2));
    const total = subtotal + tax;
    const count = items.filter(
      (item) => parseInt(item.Quantity || 0) > 0,
    ).length;

    setCartSubtotal(subtotal);
    setCartTax(tax);
    setCartTaxEnabled(includeVat);
    setCartTaxRate(rate);
    setCartTotal(total);
    setCartCount(count);
    setCartSavings(totalSavings);
    setCartOriginalTotal(originalTotal);
  }, [tenantSettings]);

  useEffect(() => {
    calculateTotals(cartItems);
  }, [calculateTotals, cartItems]);

  // ✅ Check if a promotion has expired
  const isPromotionExpired = useCallback((promotion) => {
    if (!promotion) return false;

    const now = new Date();
    const endDate = new Date(promotion.EndDate);
    const startDate = new Date(promotion.StartDate);

    // Check if promotion is active
    const isActive = promotion.IsActive !== false;
    const hasStarted = now >= startDate;
    const hasEnded = now > endDate;

    return !isActive || !hasStarted || hasEnded;
  }, []);

  // ✅ Check if a promotion is expiring soon (within 24 hours)
  const isPromotionExpiringSoon = useCallback((promotion) => {
    if (!promotion || !promotion.EndDate) return false;

    const now = new Date();
    const endDate = new Date(promotion.EndDate);
    const timeUntilExpiry = endDate - now;
    const twentyFourHours = 24 * 60 * 60 * 1000;

    // Promotion is expiring within 24 hours and hasn't expired yet
    return timeUntilExpiry > 0 && timeUntilExpiry <= twentyFourHours;
  }, []);

  // ✅ Check for expired and expiring promotions in cart items
  const checkPromotionStatus = useCallback(
    (items) => {
      const expired = [];
      const expiring = [];

      items.forEach((item) => {
        if (item.Promotion) {
          if (isPromotionExpired(item.Promotion)) {
            expired.push({
              name: item.Name || item.name,
              promotionCode: item.Promotion.Code,
              oldPrice: item.OriginalPrice,
              newPrice: item.UnitPrice || item.Price,
              productId: item.ProductID || item.productId,
            });
          } else if (isPromotionExpiringSoon(item.Promotion)) {
            expiring.push({
              name: item.Name || item.name,
              promotionCode: item.Promotion.Code,
              endDate: item.Promotion.EndDate,
              discountValue: item.Promotion.DiscountValue,
              discountType: item.Promotion.DiscountType,
              productId: item.ProductID || item.productId,
            });
          }
        }
      });

      setExpiredPromotions(expired);
      setExpiringPromotions(expiring);

      return { expired, expiring };
    },
    [isPromotionExpired, isPromotionExpiringSoon],
  );

  // ✅ Show notification for expired promotions
  const notifyExpiredPromotions = useCallback((expiredItems) => {
    if (expiredItems.length === 0 || expiredNotifiedRef.current) return;

    expiredNotifiedRef.current = true;

    const message = expiredItems
      .map(
        (item) =>
          `${item.name} (${item.promotionCode}) - was R${item.oldPrice.toFixed(2)}, now R${item.newPrice.toFixed(2)}`,
      )
      .join("\n");

    toast.error(
      (t) => (
        <div style={{ maxWidth: "400px" }}>
          <strong
            style={{ color: "#ef4444", display: "block", marginBottom: "8px" }}
          >
            ⚠️ Promotion(s) Expired!
          </strong>
          <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
            {expiredItems.map((item, index) => (
              <div key={index} style={{ marginBottom: "4px" }}>
                <span style={{ fontWeight: "500" }}>{item.name}</span>
                <span style={{ color: "#ef4444", marginLeft: "4px" }}>
                  ({item.promotionCode} expired)
                </span>
                <br />
                <span style={{ fontSize: "13px", color: "#4b5563" }}>
                  R{item.oldPrice.toFixed(2)} → R{item.newPrice.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{
              marginTop: "8px",
              padding: "4px 16px",
              background: "#2c7da0",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Got it
          </button>
        </div>
      ),
      { duration: 8000, id: "promotion-expired" },
    );
  }, []);

  // ✅ Apply promotions to cart items
  const applyPromotionsToCart = useCallback(
    (items, promotions) => {
      if (!items || items.length === 0) return items;

      console.log("🔄 Applying promotions to cart items...");
      console.log("📊 Available promotions:", promotions.size);

      const processedItems = items.map((item) => {
        const productId = item.ProductID || item.productId || item.id;

        // Check if this product has a promotion
        const promotion = promotions?.get(productId) || null;

        if (promotion) {
          const originalPrice = parseFloat(
            item.OriginalPrice ||
              item.UnitPrice ||
              item.Price ||
              item.SellingPrice ||
              0,
          );
          const discountValue = parseFloat(promotion.DiscountValue) || 0;
          let discountedPrice = originalPrice;

          if (promotion.DiscountType === "Percentage") {
            discountedPrice =
              Math.round(originalPrice * (1 - discountValue / 100) * 100) / 100;
          } else if (promotion.DiscountType === "FixedAmount") {
            discountedPrice = Math.max(
              0,
              Math.round((originalPrice - discountValue) * 100) / 100,
            );
          }

          const discountApplied = originalPrice - discountedPrice;

          console.log(
            `  📦 ${item.Name || item.name}: R${originalPrice} -> R${discountedPrice} (${discountValue}% off) [${promotion.Code}]`,
          );

          return {
            ...item,
            UnitPrice: discountedPrice,
            Price: discountedPrice,
            SellingPrice: discountedPrice,
            OriginalPrice: originalPrice,
            DiscountApplied: discountApplied,
            Promotion: promotion,
            PromotionID: promotion.PromotionID,
            PromotionCode: promotion.Code,
            DiscountType: promotion.DiscountType,
            DiscountValue: promotion.DiscountValue,
          };
        }

        return item;
      });

      // ✅ Check promotion status
      const { expired, expiring } = checkPromotionStatus(processedItems);

      // ✅ Notify about expired promotions
      if (expired.length > 0) {
        notifyExpiredPromotions(expired);
      }

      return processedItems;
    },
    [checkPromotionStatus, notifyExpiredPromotions],
  );

  // Fetch cart from server - with debouncing
  const fetchCartFromServer = useCallback(
    async (silent = false) => {
      // ✅ Always fetch promotions first
      const promotions = await fetchPromotions();

      if (!userRef.current) {
        let guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
        // ✅ Apply promotions to guest cart
        guestCart = applyPromotionsToCart(guestCart, promotions);
        if (isMounted.current) {
          setCartItems(guestCart);
          calculateTotals(guestCart);
        }
        return;
      }

      // Debounce multiple rapid calls
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }

      return new Promise((resolve) => {
        fetchTimeout.current = setTimeout(
          async () => {
            try {
              if (!silent) {
                setLoading(true);
              }

              console.log("📡 Fetching cart from server...");
              const response = await api.get("/api/cart");
              let items = [];

              if (
                response.data &&
                response.data.cart &&
                response.data.cart.items
              ) {
                items = response.data.cart.items;
              } else if (response.data && response.data.items) {
                items = response.data.items;
              } else if (
                response.data &&
                response.data.success &&
                response.data.cart
              ) {
                items = response.data.cart.items || [];
              } else if (Array.isArray(response.data)) {
                items = response.data;
              }

              // ✅ Apply promotions to server cart items
              items = applyPromotionsToCart(items, promotions);
              console.log(
                `✅ Cart has ${items.length} items after applying promotions`,
              );

              if (isMounted.current) {
                setCartItems(items);
                calculateTotals(items);
              }
              resolve(items);
            } catch (error) {
              console.error("Error fetching cart:", error);
              // Keep the hydrated cart when auth or the network is unavailable.
              resolve([]);
            } finally {
              if (isMounted.current) {
                setLoading(false);
              }
              fetchTimeout.current = null;
            }
          },
          silent ? 0 : 100,
        );
      });
    },
    [calculateTotals, applyPromotionsToCart, fetchPromotions],
  );

  // Emit cart update event
  const emitCartUpdate = useCallback(() => {
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  }, []);

  // Merge guest cart with user cart
  const mergeGuestCartWithUser = useCallback(async () => {
    if (!user) {
      console.log("No user logged in, skipping cart merge");
      return;
    }

    try {
      const localCart = JSON.parse(localStorage.getItem("cart") || "[]");
      const pendingCart = localStorage.getItem("guestCartPendingMerge");
      const guestCart = pendingCart ? JSON.parse(pendingCart) : localCart;

      if (guestCart.length === 0) {
        console.log("No guest cart items to merge");
        localStorage.removeItem("guestCartPendingMerge");
        await fetchCartFromServer();
        return true;
      }

      console.log("Merging guest cart items:", guestCart);

      let mergedCount = 0;
      const failedItems = [];
      const mergePromises = guestCart.map(async (item) => {
        const productId = item.ProductID || item.productId || item.id;
        if (!productId) {
          failedItems.push(item);
          return;
        }

        try {
          const quantity = item.Quantity || item.quantity || 1;
          const promotionId = item.PromotionID || null;

          const response = await api.post("/api/cart/add", {
            productId,
            quantity,
            promotionId,
            price: item.UnitPrice || item.Price || 0,
            originalPrice: item.OriginalPrice || item.UnitPrice || 0,
          });
          if (response.data?.success === false) {
            throw new Error(response.data.message || "Cart item was rejected");
          }
          mergedCount++;
        } catch (error) {
          console.error("Error adding item to cart:", error);
          failedItems.push(item);
        }
      });

      await Promise.all(mergePromises);

      if (failedItems.length > 0) {
        const cartToKeep = localCart.length > 0 ? localCart : guestCart;
        localStorage.setItem(
          "guestCartPendingMerge",
          JSON.stringify(failedItems),
        );
        localStorage.setItem("cart", JSON.stringify(cartToKeep));
        setCartItems(cartToKeep);
        calculateTotals(cartToKeep);
        throw new Error(`Failed to merge ${failedItems.length} cart item(s)`);
      }

      localStorage.removeItem("guestCartPendingMerge");
      localStorage.removeItem("cart");
      console.log(`Successfully merged ${mergedCount} items from guest cart`);
      await fetchCartFromServer();
      emitCartUpdate();
      return true;
    } catch (error) {
      console.error("Error merging guest cart:", error);
      throw error;
    }
  }, [user, fetchCartFromServer, emitCartUpdate, calculateTotals]);

  // Add item to cart - with promotion support
  const addToCart = useCallback(
    async (productId, quantity = 1, productData = {}) => {
      try {
        // Extract promotion data
        const promotion = productData.promotion || null;
        let originalPrice = parseFloat(
          productData.originalPrice ||
            productData.Price ||
            productData.UnitPrice ||
            0,
        );
        let finalPrice = parseFloat(
          productData.price || productData.Price || productData.UnitPrice || 0,
        );

        // If no originalPrice provided, use finalPrice
        if (!productData.originalPrice && !productData.OriginalPrice) {
          originalPrice = finalPrice;
        }

        // If promotion exists but price wasn't calculated, calculate it
        if (promotion && finalPrice === originalPrice) {
          const discountValue = parseFloat(promotion.DiscountValue) || 0;
          if (promotion.DiscountType === "Percentage") {
            finalPrice =
              Math.round(originalPrice * (1 - discountValue / 100) * 100) / 100;
          } else if (promotion.DiscountType === "FixedAmount") {
            finalPrice = Math.max(
              0,
              Math.round((originalPrice - discountValue) * 100) / 100,
            );
          }
        }

        const discountApplied = Math.max(0, originalPrice - finalPrice);
        const promotionId = promotion?.PromotionID || promotion?.id || null;

        console.log(`📦 Adding to cart: ${productData.name || "Product"}`, {
          originalPrice,
          finalPrice,
          discountApplied,
          hasPromotion: !!promotion,
          promotionCode: promotion?.Code,
        });

        // For guest users
        if (!user) {
          const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
          const existingItemIndex = guestCart.findIndex(
            (item) =>
              item.ProductID === productId || item.productId === productId,
          );

          const newItem = {
            ProductID: productId,
            productId: productId,
            Quantity: quantity,
            quantity: quantity,
            Name: productData.name || productData.Name || "Product",
            UnitPrice: finalPrice,
            Price: finalPrice,
            SellingPrice: finalPrice,
            OriginalPrice: originalPrice,
            DiscountApplied: discountApplied,
            PromotionID: promotionId,
            Promotion: promotion,
            ImageUrl: productData.imageUrl || productData.ImageUrl || "",
            SKU: productData.sku || productData.SKU || "",
          };

          if (existingItemIndex >= 0) {
            guestCart[existingItemIndex].Quantity =
              (guestCart[existingItemIndex].Quantity || 1) + quantity;
            guestCart[existingItemIndex].UnitPrice = finalPrice;
            guestCart[existingItemIndex].Price = finalPrice;
            guestCart[existingItemIndex].SellingPrice = finalPrice;
            guestCart[existingItemIndex].OriginalPrice = originalPrice;
            guestCart[existingItemIndex].DiscountApplied = discountApplied;
            guestCart[existingItemIndex].PromotionID = promotionId;
            guestCart[existingItemIndex].Promotion = promotion;
          } else {
            guestCart.push(newItem);
          }

          localStorage.setItem("cart", JSON.stringify(guestCart));
          setCartItems(guestCart);
          calculateTotals(guestCart);
          trackMarketingEvent("add_to_cart", {
            productId,
            productName: productData.name || productData.Name || "Product",
            quantity,
            value: finalPrice * quantity,
          });

          if (promotion) {
            toast.success(
              `Added to cart with ${promotion.DiscountType === "Percentage" ? `${promotion.DiscountValue}% off` : "discount"}!`,
              { duration: 1500 },
            );
          } else {
            toast.success("Added to cart", { duration: 1500 });
          }

          emitCartUpdate();
          return { success: true };
        }

        // For logged-in users
        setLoadingItems((prev) => ({ ...prev, [productId]: true }));

        // API call with promotion data
        const response = await api.post("/api/cart/add", {
          productId,
          quantity,
          promotionId: promotionId,
          price: finalPrice,
          originalPrice: originalPrice,
        });

        if (response.data.success) {
          // ✅ Fetch updated cart from server
          await fetchCartFromServer(true);
          trackMarketingEvent("add_to_cart", {
            productId,
            productName: productData.name || productData.Name || "Product",
            quantity,
            value: finalPrice * quantity,
          });

          if (promotion) {
            toast.success(
              `Added to cart with ${promotion.DiscountType === "Percentage" ? `${promotion.DiscountValue}% off` : "discount"}!`,
              { duration: 1500 },
            );
          } else {
            toast.success("Added to cart", { duration: 1500 });
          }

          emitCartUpdate();
          return response.data;
        } else {
          await fetchCartFromServer();
          toast.error("Failed to add item", { duration: 2000 });
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        await fetchCartFromServer();
        toast.error("Failed to add item to cart", { duration: 2000 });
        throw error;
      } finally {
        setLoadingItems((prev) => ({ ...prev, [productId]: false }));
      }
    },
    [user, fetchCartFromServer, calculateTotals, emitCartUpdate],
  );

  // Update quantity
  const updateQuantity = useCallback(
    async (productId, quantity) => {
      try {
        if (!user) {
          const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
          const itemIndex = guestCart.findIndex(
            (item) =>
              item.ProductID === productId || item.productId === productId,
          );

          if (itemIndex >= 0) {
            if (quantity <= 0) {
              guestCart.splice(itemIndex, 1);
            } else {
              guestCart[itemIndex].Quantity = quantity;
              guestCart[itemIndex].quantity = quantity;
            }
            localStorage.setItem("cart", JSON.stringify(guestCart));
            setCartItems(guestCart);
            calculateTotals(guestCart);
          }
          return;
        }

        setLoadingItems((prev) => ({ ...prev, [productId]: true }));

        // Optimistic update
        const updatedItems = cartItems.map((item) => {
          const itemId = item.ProductID || item.productId || item.id;
          if (itemId === productId) {
            return { ...item, Quantity: quantity };
          }
          return item;
        });
        setCartItems(updatedItems);
        calculateTotals(updatedItems);

        const item = cartItems.find(
          (i) => i.ProductID === productId || i.productId === productId,
        );
        if (item) {
          const cartItemId = item.CartItemID || item.cartItemId || item.id;
          const response = await api.put(`/api/cart/update/${cartItemId}`, {
            quantity,
          });
          if (response.data.success) {
            fetchCartFromServer(true);
            emitCartUpdate();
          }
        }
      } catch (error) {
        console.error("Error updating quantity:", error);
        await fetchCartFromServer();
        toast.error("Failed to update quantity", { duration: 2000 });
        throw error;
      } finally {
        setLoadingItems((prev) => ({ ...prev, [productId]: false }));
      }
    },
    [user, cartItems, fetchCartFromServer, calculateTotals, emitCartUpdate],
  );

  // Remove from cart
  const removeFromCart = useCallback(
    async (productId) => {
      setDeletingItemId(productId);
      setLoadingItems((prev) => ({ ...prev, [productId]: true }));

      try {
        if (!user) {
          const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
          const filteredCart = guestCart.filter(
            (item) =>
              item.ProductID !== productId && item.productId !== productId,
          );
          localStorage.setItem("cart", JSON.stringify(filteredCart));
          setCartItems(filteredCart);
          calculateTotals(filteredCart);
          toast.success("Item removed", { duration: 1500 });
          emitCartUpdate();
          return;
        }

        // Optimistic update - remove immediately
        const filteredItems = cartItems.filter(
          (i) => i.ProductID !== productId && i.productId !== productId,
        );
        setCartItems(filteredItems);
        calculateTotals(filteredItems);

        const item = cartItems.find(
          (i) => i.ProductID === productId || i.productId === productId,
        );
        if (item) {
          const cartItemId = item.CartItemID || item.cartItemId || item.id;
          const response = await api.delete(`/api/cart/remove/${cartItemId}`);

          if (response.data.success) {
            fetchCartFromServer(true);
            emitCartUpdate();
            toast.success("Item removed", { duration: 1500 });
          } else {
            await fetchCartFromServer();
            toast.error("Failed to remove", { duration: 2000 });
          }
        }
      } catch (error) {
        console.error("Error removing from cart:", error);
        await fetchCartFromServer();
        toast.error("Failed to remove", { duration: 2000 });
        throw error;
      } finally {
        setTimeout(() => {
          setDeletingItemId(null);
          setLoadingItems((prev) => ({ ...prev, [productId]: false }));
        }, 200);
      }
    },
    [user, cartItems, fetchCartFromServer, calculateTotals, emitCartUpdate],
  );

  // Clear cart
  const clearCart = useCallback(async () => {
    try {
      if (!user) {
        localStorage.removeItem("cart");
        setCartItems([]);
        calculateTotals([]);
        toast.success("Cart cleared", { duration: 1500 });
        emitCartUpdate();
        return;
      }

      setLoading(true);

      // Optimistic update - clear immediately
      setCartItems([]);
      calculateTotals([]);

      const response = await api.delete("/api/cart/clear");
      if (response.data.success) {
        emitCartUpdate();
        toast.success("Cart cleared", { duration: 1500 });
      } else {
        await fetchCartFromServer();
        toast.error("Failed to clear cart", { duration: 2000 });
      }
    } catch (error) {
      console.error("Error clearing cart:", error);
      await fetchCartFromServer();
      toast.error("Failed to clear cart", { duration: 2000 });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, fetchCartFromServer, calculateTotals, emitCartUpdate]);

  // ✅ Get quantity of a specific product in cart
  const getProductQuantity = useCallback(
    (productId) => {
      if (!productId) return 0;
      const item = cartItems.find(
        (item) => (item.ProductID || item.productId || item.id) === productId,
      );
      return item ? parseInt(item.Quantity || 0) : 0;
    },
    [cartItems],
  );

  // ✅ Get all product quantities as a map
  const getProductQuantities = useCallback(() => {
    const map = new Map();
    cartItems.forEach((item) => {
      const id = item.ProductID || item.productId || item.id;
      if (id) {
        map.set(id, parseInt(item.Quantity || 0));
      }
    });
    return map;
  }, [cartItems]);

  // Reset expired notification flag when items change
  useEffect(() => {
    expiredNotifiedRef.current = false;
  }, [cartItems]);

  // Merge a guest cart before fetching the authenticated cart after sign-in.
  useEffect(() => {
    if (user && localStorage.getItem("mergeGuestCartAfterLogin") === "true") {
      mergeGuestCartWithUser()
        .then(() => {
          localStorage.removeItem("mergeGuestCartAfterLogin");
        })
        .catch((error) => {
          console.error("Could not sync guest cart after sign-in:", error);
        });
      return;
    }

    fetchCartFromServer();
  }, [user, fetchCartFromServer, mergeGuestCartWithUser]);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const value = {
    cartItems,
    cartSubtotal,
    cartTax,
    cartTaxEnabled,
    cartTaxRate,
    cartTotal,
    cartCount,
    cartSavings,
    cartOriginalTotal,
    loading,
    loadingItems,
    deletingItemId,
    expiredPromotions,
    expiringPromotions,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCartFromServer,
    mergeGuestCartWithUser,
    getProductQuantity, // ✅ Added
    getProductQuantities, // ✅ Added
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
